import type { FC } from 'react'

import { LoaderCircle, StyledText } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import RouterLink from '@/components/RouterLink'
import queries from '@/modules/admin/queries'
import { leasingResources } from '@/resources/leasing'
import { APP_ROUTES } from '@/util/app-routes'

import { EndOfLeaseHistory } from './EndOfLeaseHistory'

export const EndOfLease: FC = () => {
  const params = useParams()
  const assetId = Number(params.id)
  const translate = useTranslation()
  const navigate = useNavigate()

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  const { data, isLoading, isError } = useQuery(queries.adminLeasingAssetDetails(assetId), () =>
    leasingResources.getLeasingAssetDetails(assetId)
  )

  if (isLoading) {
    return <LoaderCircle />
  }
  if (isError) {
    return <LoadingError />
  }

  const currentEndOfLeaseWorkflow = data?.currentEndOfLeaseWorkflow
  const workflowHistory = data?.endOfLeaseWorkflowHistory

  if (!data || (!currentEndOfLeaseWorkflow && !workflowHistory?.length)) {
    navigate(APP_ROUTES.HARDWARE_ASSET.replace(':id', String(assetId)))
    return null
  }

  const isAnyDate =
    currentEndOfLeaseWorkflow?.processStartDate || currentEndOfLeaseWorkflow?.endOfLeaseDate
  const isAnyEmployeeSelection =
    currentEndOfLeaseWorkflow?.employeeDecisionOpenDate ||
    currentEndOfLeaseWorkflow?.employeeDecisionDeadlineDate
  const isAnyProcess =
    currentEndOfLeaseWorkflow?.isActive ||
    currentEndOfLeaseWorkflow?.processStartedDatetime ||
    currentEndOfLeaseWorkflow?.workflowStep

  return (
    <div className="-mx-2 -my-4 flex flex-wrap">
      {(isAnyDate || isAnyEmployeeSelection) && (
        <section
          className={twJoin(
            'shadow-basic m-2 flex flex-1 items-start rounded-md bg-white p-4',
            isAnyProcess ||
              currentEndOfLeaseWorkflow?.completedDatetime ||
              currentEndOfLeaseWorkflow?.orderId ||
              currentEndOfLeaseWorkflow?.cancelledDatetime
              ? 'basis-[30rem]'
              : 'basis-full'
          )}
        >
          <DataPoints>
            {isAnyDate && (
              <div className="flex basis-[30rem]">
                {currentEndOfLeaseWorkflow?.processStartDate && (
                  <DataPoint
                    header={<Translation id="End of lease start date" />}
                    value={<LocalDateTime date={currentEndOfLeaseWorkflow.processStartDate} />}
                  />
                )}
                {currentEndOfLeaseWorkflow?.endOfLeaseDate && (
                  <DataPoint
                    header={<Translation id="End of lease date" />}
                    value={<LocalDateTime date={currentEndOfLeaseWorkflow.endOfLeaseDate} />}
                  />
                )}
              </div>
            )}

            {isAnyEmployeeSelection && (
              <>
                <h3>
                  <Translation id="Employee selection" />
                </h3>
                <div className="flex basis-[30rem]">
                  {currentEndOfLeaseWorkflow?.employeeDecisionOpenDate && (
                    <DataPoint
                      header={<Translation id="Open date" />}
                      value={
                        <LocalDate date={currentEndOfLeaseWorkflow.employeeDecisionOpenDate} />
                      }
                    />
                  )}
                  {currentEndOfLeaseWorkflow?.employeeDecisionDeadlineDate && (
                    <DataPoint
                      header={<Translation id="Deadline date" />}
                      value={
                        <LocalDate date={currentEndOfLeaseWorkflow.employeeDecisionDeadlineDate} />
                      }
                    />
                  )}
                </div>
              </>
            )}
          </DataPoints>
        </section>
      )}

      {(isAnyProcess ||
        currentEndOfLeaseWorkflow?.completedDatetime ||
        currentEndOfLeaseWorkflow?.orderId ||
        currentEndOfLeaseWorkflow?.cancelledDatetime) && (
        <section className="shadow-basic m-2 flex flex-1 basis-[30rem] rounded-md bg-white p-4">
          <DataPoints>
            {isAnyProcess && (
              <>
                <StyledText colorMode="black" size="large" className="m-2 mt-0">
                  <Translation id="Process" />
                </StyledText>
                <div className="w-full flex-col flex-wrap">
                  {currentEndOfLeaseWorkflow?.isActive && (
                    <DataPoint
                      header={<Translation id="Active" />}
                      value={currentEndOfLeaseWorkflow.isActive ? 'Is active' : 'Not active'}
                    />
                  )}
                  {currentEndOfLeaseWorkflow?.processStartedDatetime && (
                    <DataPoint
                      header={<Translation id="Process started date time" />}
                      value={
                        <LocalDateTime date={currentEndOfLeaseWorkflow.processStartedDatetime} />
                      }
                    />
                  )}
                  {currentEndOfLeaseWorkflow?.workflowStep && (
                    <DataPoint
                      header={<Translation id="Workflow step" />}
                      value={<Translation id={currentEndOfLeaseWorkflow.workflowStep} />}
                    />
                  )}
                </div>
              </>
            )}

            {(currentEndOfLeaseWorkflow?.completedDatetime ||
              currentEndOfLeaseWorkflow?.orderId) && (
              <div className="flex basis-[30rem]">
                {currentEndOfLeaseWorkflow?.completedDatetime && (
                  <DataPoint
                    header={<Translation id="Completed date time" />}
                    value={<LocalDateTime date={currentEndOfLeaseWorkflow.completedDatetime} />}
                  />
                )}
                {currentEndOfLeaseWorkflow?.orderId && (
                  <DataPoint
                    header={<Translation id="Order Id" />}
                    value={
                      <RouterLink
                        to={APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER.replace(
                          ':ecomPolicyOrderId',
                          String(currentEndOfLeaseWorkflow.orderId)
                        )}
                        hasUnderline={false}
                      >
                        <div className="text-lg">{currentEndOfLeaseWorkflow.orderId}</div>
                      </RouterLink>
                    }
                  />
                )}
              </div>
            )}
            {currentEndOfLeaseWorkflow?.cancelledDatetime && (
              <div className="w-full">
                <DataPoint
                  header={<Translation id="Cancelled date time" />}
                  value={<LocalDateTime date={currentEndOfLeaseWorkflow.cancelledDatetime} />}
                />
              </div>
            )}
          </DataPoints>
        </section>
      )}

      {workflowHistory && workflowHistory.length > 0 && (
        <EndOfLeaseHistory history={workflowHistory} />
      )}
    </div>
  )
}
